import React, { useState } from 'react';
import { Box, VStack, Text, Image, IconButton, Icon, Carousel, NativeSelect } from '@chakra-ui/react';
import { LuArrowLeft, LuArrowRight } from 'react-icons/lu';
import type { DonationForm, DonationFrequency } from '@kuow/core';
import { CheckboxCircle } from '../primitives/CheckboxCircle';

type FormGift = DonationForm['gifts'][number];

export interface GiftCarouselProps {
  gifts: FormGift[];
  frequency: DonationFrequency;
  currentDonationAmount: number;
  selectedGiftId: number | null;
  selectedVariantId?: number;
  onGiftSelect: (giftId: number) => void;
  onVariantSelect: (giftId: number, variantId: number) => void;
}

export const GiftCarousel: React.FC<GiftCarouselProps> = ({
  gifts,
  frequency,
  currentDonationAmount,
  selectedGiftId,
  selectedVariantId,
  onGiftSelect,
  onVariantSelect,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [expandedGiftId, setExpandedGiftId] = useState<number | null>(null);

  // Sync the carousel to the selected gift only when the selection itself
  // changes (e.g., the donor clicks a card, or selectedGiftId is set via
  // initialSelection). Do NOT depend on activeIndex here — otherwise every
  // time the donor pages the carousel via the prev/next buttons, this effect
  // would snap them back to the selected gift, fighting their navigation.
  React.useEffect(() => {
    if (!gifts.length || selectedGiftId === null) {
      return;
    }

    const index = gifts.findIndex((gift) => gift.id === selectedGiftId);
    if (index >= 0) {
      setActiveIndex(index);
    }
  }, [gifts, selectedGiftId]);

  if (gifts.length === 0) {
    return null;
  }

  const priceField: 'price_per_monthly' | 'price_per_single' =
    frequency === 'monthly' ? 'price_per_monthly' : 'price_per_single';

  return (
    <Box
      position="relative"
      w={{
        base: 'calc(100% + var(--spacing-3) * 2)',
        _cqMd: 'calc(100% + var(--spacing-6) * 2)',
        _cqLg: 'calc(100% + var(--spacing-12) * 2)',
      }}
      overflow="visible"
      mx={{ base: '-3', _cqMd: '-6', _cqLg: '-12' }}
    >
      <Carousel.Root
        slideCount={gifts.length}
        page={activeIndex}
        onPageChange={(event) => {
          setActiveIndex(event.page);
        }}
        slidesPerPage={1}
        loop={true}
        spacing="48px"
      >
        <Carousel.ItemGroup w="full" display="flex" alignItems="stretch" overflow="visible" px="calc((100% - 200px) / 2)">
        {gifts.map((gift, index) => {
          const isSelected = selectedGiftId === gift.id;
          const minPrice = parseFloat(gift[priceField]);
          const donationAmountValue = Number.isFinite(currentDonationAmount) ? currentDonationAmount : 0;
          const additionalAmount = Math.max(minPrice - donationAmountValue, 0);
          const additionalAmountLabel = `+ $${additionalAmount}${frequency === 'monthly' ? '/mo' : ''}`;
          const isDetailsVisible = expandedGiftId === gift.id;

          const selectedVariant = isSelected && selectedVariantId
            ? gift.gift_variants.find((v) => v.id === selectedVariantId)
            : null;
          const carouselImage = selectedVariant?.image ?? gift.image;

          return (
            <Carousel.Item key={gift.id} index={index} snapAlign="center" w="200px" display="flex" alignItems="stretch" flexShrink={0}>
              <VStack
                as="button"
                onClick={(event) => {
                  event.preventDefault();
                  if (!isSelected) {
                    onGiftSelect(gift.id);
                  }
                }}
                cursor="pointer"
                borderWidth={1}
                borderRadius="md"
                borderColor={isSelected ? 'river.solid' : 'border'}
                bg="bg"
                textAlign="center"
                w="full"
                h="full"
                overflow="hidden"
                align="stretch"
                gap="2"
                pt={0}
                px={0}
                pb="2"
                _hover={{
                  bg: 'rgba(0, 0, 0, 0.02)'
                }}
              >
                {/* Image - only show in collapsed view */}
                {!isDetailsVisible && carouselImage && (
                  <Box h="134px" w="full" overflow="hidden" flexShrink={0}>
                    <Image
                      src={carouselImage.cdn_url}
                      alt={gift.name}
                      objectFit="contain"
                      w="full"
                      h="full"
                    />
                  </Box>
                )}

                {/* Info section */}
                <VStack align="center" gap="1.5" px="2.5" pt={isDetailsVisible ? "5" : "0"} flex="1" minH="0">
                  <Text 
                    fontWeight="medium" 
                    fontSize="md"
                    lineHeight="1.2"
                    textAlign="center"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    w="full"
                    flexShrink={0}
                  >
                    {gift.name}
                  </Text>

                  {isDetailsVisible ? (
                    <Box 
                      flex="1"
                      overflowY="auto"
                      w="full"
                      minH="0"
                      css={{
                        '&::-webkit-scrollbar': {
                          width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#d9d9d9',
                          borderRadius: '10px',
                        },
                      }}
                    >
                      <Text 
                        fontSize="xs" 
                        color="fg.muted" 
                        lineHeight="1.33"
                        fontWeight="normal"
                        textAlign="left"
                      >
                        {gift.long_description || gift.description}
                      </Text>
                    </Box>
                  ) : (
                    <Box flex="1" w="full" display="flex" alignItems="start" justifyContent="center">
                      <Text 
                        fontSize="xs" 
                        color="fg.muted"
                        lineHeight="1.33"
                        fontWeight="normal"
                        textAlign="center"
                        overflow="hidden"
                        w="full"
                        css={{
                          display: '-webkit-box',
                          WebkitLineClamp: '2',
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {gift.description}
                      </Text>
                    </Box>
                  )}

                  <Text
                    as="span"
                    role="button"
                    tabIndex={0}
                    fontSize="xs"
                    fontWeight="medium"
                    lineHeight="1.2"
                    textDecoration="underline"
                    textDecorationSkipInk="none"
                    textUnderlinePosition="from-font"
                    textAlign="center"
                    color="fg"
                    flexShrink={0}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setExpandedGiftId(isDetailsVisible ? null : gift.id);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        setExpandedGiftId(isDetailsVisible ? null : gift.id);
                      }
                    }}
                  >
                    {isDetailsVisible ? 'Hide Details' : 'View Details'}
                  </Text>
                </VStack>

                {/* Select section */}
                <VStack align="center" justify="center" gap="1.5" px="5" flexShrink={0}>
                  <Box h="8" display="flex" alignItems="center" justifyContent="center" w="full">
                    {isSelected && gift.gift_variants.length > 1 ? (
                      <NativeSelect.Root 
                        size="xs" 
                        w="full"
                        borderColor={selectedVariantId ? 'river.solid' : 'border'}
                        borderWidth={1}
                        borderRadius="sm"
                      >
                        <NativeSelect.Field
                          value={selectedVariantId ? String(selectedVariantId) : ''}
                          placeholder="Option"
                          onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                            const value = event.currentTarget.value;
                            if (!value) {
                              return;
                            }
                            const variantId = Number.parseInt(value, 10);
                            if (!Number.isNaN(variantId)) {
                              onVariantSelect(gift.id, variantId);
                            }
                          }}
                        >
                          {gift.gift_variants.map((variant) => (
                            <option key={variant.id} value={variant.id}>
                              {variant.name}
                            </option>
                          ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    ) : (
                      <CheckboxCircle isSelected={isSelected} />
                    )}
                  </Box>
                  
                  <Text
                    fontSize="xs"
                    fontWeight="medium"
                    lineHeight="1.33"
                    color="fg"
                    textAlign="center"
                  >
                    {additionalAmountLabel}
                  </Text>
                </VStack>
              </VStack>
            </Carousel.Item>
          );
        })}
      </Carousel.ItemGroup>

      <Carousel.PrevTrigger asChild>
        <IconButton 
          aria-label="Previous gift" 
          variant="solid"
          colorPalette="gray"
          rounded="full"
          size="md"
          bg="gray.solid"
          color="gray.contrast"
          position="absolute"
          left="4"
          top="50%"
          transform="translateY(-50%)"
          zIndex={10}
          _hover={{ bg: 'gray.emphasized' }}
        >
          <Icon as={LuArrowLeft} />
        </IconButton>
      </Carousel.PrevTrigger>

      <Carousel.NextTrigger asChild>
        <IconButton 
          aria-label="Next gift" 
          variant="solid"
          colorPalette="gray"
          rounded="full"
          size="md"
          bg="gray.solid"
          color="gray.contrast"
          position="absolute"
          right="4"
          top="50%"
          transform="translateY(-50%)"
          zIndex={10}
          _hover={{ bg: 'gray.emphasized' }}
        >
          <Icon as={LuArrowRight} />
        </IconButton>
      </Carousel.NextTrigger>
    </Carousel.Root>
    </Box>
  );
};


